import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

export function useBrowser() {
    /**
     * Open URL in in-app browser (native) or new tab (web)
     * @param url - The URL to open
     * @param options - Optional configuration for browser presentation
     * @param options.presentationStyle - How the browser should be presented (fullscreen or popover)
     * @param options.toolbarColor - Color of the browser toolbar (hex format)
     */
    const openUrl = async (
        url: string,
        options?: {
            presentationStyle?: 'fullscreen' | 'popover';
            toolbarColor?: string;
        },
    ) => {
        if (Capacitor.isNativePlatform()) {
            // Native app - use Capacitor Browser
            await Browser.open({
                url,
                presentationStyle: options?.presentationStyle || 'popover',
                toolbarColor: options?.toolbarColor,
            });
        } else {
            // Web - use window.open
            window.open(url, '_blank');
        }
    };

    /**
     * Close the in-app browser
     * Only works on native platforms
     */
    const closeBrowser = async () => {
        if (Capacitor.isNativePlatform()) {
            await Browser.close();
        }
    };

    /**
     * Add listener for browser finished event
     * Only works on native platforms
     * @param callback - Function to call when browser is closed
     */
    const addBrowserFinishedListener = (callback: () => void) => {
        if (Capacitor.isNativePlatform()) {
            Browser.addListener('browserFinished', callback);
        }
    };

    /**
     * Add listener for browser page loaded event
     * Only works on native platforms
     * @param callback - Function to call when page loads
     */
    const addBrowserPageLoadedListener = (callback: () => void) => {
        if (Capacitor.isNativePlatform()) {
            Browser.addListener('browserPageLoaded', callback);
        }
    };

    /**
     * Remove all browser listeners
     * Only works on native platforms
     * Call this in component cleanup to prevent memory leaks
     */
    const removeAllListeners = () => {
        if (Capacitor.isNativePlatform()) {
            Browser.removeAllListeners();
        }
    };

    return {
        openUrl,
        closeBrowser,
        addBrowserFinishedListener,
        addBrowserPageLoadedListener,
        removeAllListeners,
    };
}
